import {
  getCallableFunctionUrl,
  getRecordingFirebaseConfig,
} from "../constants/recordingFirebaseConfig";
import { getEnv } from "helpers/env";

type CachedFirebaseAuth = {
  expiresAt: number;
  hybridTokenKey: string;
  idToken: string;
};

type ExchangeFirebaseTokenResponse = {
  firebaseToken: string;
};

type FirebaseCallableError = {
  message: string;
  status: string;
};

class FirebaseCallableRequestError extends Error {
  readonly functionName?: string;
  readonly firebaseStatus?: string;
  readonly httpStatus?: number;

  constructor(
    message: string,
    options?: {
      functionName?: string;
      firebaseStatus?: string;
      httpStatus?: number;
    }
  ) {
    super(message);
    this.name = "FirebaseCallableRequestError";
    this.functionName = options?.functionName;
    this.firebaseStatus = options?.firebaseStatus;
    this.httpStatus = options?.httpStatus;
  }
}

type FirebaseCallableResponse<T> = {
  error?: FirebaseCallableError;
  result?: T;
};

type SignInWithCustomTokenResponse = {
  expiresIn: string;
  idToken: string;
};

let cachedAuth: CachedFirebaseAuth | null = null;

const clearCachedFirebaseAuth = () => {
  cachedAuth = null;
};

const parseCallableResponse = <T>(
  functionName: string,
  response: FirebaseCallableResponse<T>
): T => {
  if (response.error) {
    throw new FirebaseCallableRequestError(response.error.message, {
      functionName,
      firebaseStatus: response.error.status,
    });
  }

  if (response.result === undefined) {
    throw new FirebaseCallableRequestError(
      `Firebase function ${functionName} returned no result`,
      { functionName }
    );
  }

  return response.result;
};

const postJson = async <T>(
  url: string,
  body: unknown,
  headers: Record<string, string> = {}
): Promise<T> => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let responseBody: string | undefined;
    try {
      responseBody = await response.text();
    } catch {
      responseBody = undefined;
    }

    const detail = responseBody ? `: ${responseBody.slice(0, 500)}` : "";
    throw new FirebaseCallableRequestError(
      `Request to ${url} failed with status ${response.status}${detail}`,
      { httpStatus: response.status }
    );
  }

  return (await response.json()) as T;
};

const getFirebaseIdToken = async (hybridToken: string): Promise<string> => {
  if (!hybridToken) {
    throw new FirebaseCallableRequestError(
      "No hybrid token available for Firebase authentication",
      { functionName: "exchange_firebase_token" }
    );
  }

  const now = Date.now();
  const refreshBufferMs = 60000;

  if (
    cachedAuth &&
    cachedAuth.hybridTokenKey === hybridToken &&
    cachedAuth.expiresAt - refreshBufferMs > now
  ) {
    return cachedAuth.idToken;
  }

  const env = getEnv();
  const firebaseConfig = getRecordingFirebaseConfig(env);

  const exchangeResponse = await postJson<
    FirebaseCallableResponse<ExchangeFirebaseTokenResponse>
  >(getCallableFunctionUrl("exchange_firebase_token", env), {
    data: { htAuthToken: hybridToken },
  });

  const { firebaseToken } = parseCallableResponse(
    "exchange_firebase_token",
    exchangeResponse
  );

  if (!firebaseToken) {
    throw new FirebaseCallableRequestError(
      "Failed to exchange hybrid token for Firebase custom token",
      { functionName: "exchange_firebase_token" }
    );
  }

  const signInResponse = await postJson<SignInWithCustomTokenResponse>(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken?key=${firebaseConfig.apiKey}`,
    {
      returnSecureToken: true,
      token: firebaseToken,
    }
  );

  if (!signInResponse.idToken) {
    throw new FirebaseCallableRequestError(
      "Failed to sign in with Firebase custom token",
      { functionName: "exchange_firebase_token" }
    );
  }

  cachedAuth = {
    hybridTokenKey: hybridToken,
    expiresAt: now + Number(signInResponse.expiresIn) * 1000,
    idToken: signInResponse.idToken,
  };

  return cachedAuth.idToken;
};

// An expired/invalid Firebase idToken surfaces either as an HTTP 401 from the
// callable endpoint or as an UNAUTHENTICATED status in the callable's response
// body. Both are recoverable by re-exchanging the hybrid token for a fresh
// idToken. (PERMISSION_DENIED / 403 is authorization, not authentication, so a
// refresh won't help - we deliberately don't retry those.)
const isRecoverableAuthenticationError = (error: unknown): boolean =>
  error instanceof FirebaseCallableRequestError &&
  (error.firebaseStatus === "UNAUTHENTICATED" || error.httpStatus === 401);

const callFirebaseCallable = async <TRequest, TResponse>(
  functionName: string,
  data: TRequest,
  hybridToken: string
): Promise<TResponse> => {
  const invokeOnce = async (): Promise<TResponse> => {
    const idToken = await getFirebaseIdToken(hybridToken);

    const callableResponse = await postJson<
      FirebaseCallableResponse<TResponse>
    >(
      getCallableFunctionUrl(functionName),
      {
        data,
      },
      {
        Authorization: `Bearer ${idToken}`,
      }
    );

    return parseCallableResponse(functionName, callableResponse);
  };

  try {
    return await invokeOnce();
  } catch (error) {
    if (!isRecoverableAuthenticationError(error)) {
      throw error;
    }

    // A cached Firebase idToken can go stale mid-session (e.g. a retail shift
    // outlives the token's lifetime), which otherwise makes every subsequent
    // chunk upload fail with the same auth error. Drop the cached auth so the
    // next attempt re-exchanges the hybrid token, and retry exactly once. If
    // it still fails, the error propagates and the caller treats it as fatal.
    clearCachedFirebaseAuth();
    return invokeOnce();
  }
};

export {
  callFirebaseCallable,
  clearCachedFirebaseAuth,
  FirebaseCallableRequestError,
  getFirebaseIdToken,
};
